import { useContext, useRef } from "react";
import { useMutation, useQueryClient } from "react-query";
import {
  deleteProductImage,
  reorderProductImages,
  uploadProductImage,
} from "../../../../../services/inventory";
import { useParams } from "react-router-dom";
import { ToastContext } from "../../../../../hooks/context";

export const useServiceImageUpload = ({ setRootFormDetails = () => {} }) => {
  const inputRef = useRef();
  const queryClient = useQueryClient();
  const params = useParams();
  const triggerToast = useContext(ToastContext);

  const { mutate: deleteImage } = useMutation({
    mutationFn: (mediaId) => deleteProductImage(mediaId),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["getProductDetails", params?.id]);
      queryClient.invalidateQueries(["getProductList"]);
      triggerToast(data?.data?.detail, "success");
    },
  });

  const uploadImageMutation = useMutation((data) => uploadProductImage(data));
  const isUploadingImage = uploadImageMutation.isLoading;

  const { mutate: serviceReorderImages } = useMutation((data) =>
    reorderProductImages(data),
  );

  const handleMultipleImageUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const readFile = (file) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ file, preview: reader.result });
        reader.readAsDataURL(file);
      });

    Promise.all(files.map(readFile)).then((newImages) => {
      setRootFormDetails((prev) => ({
        ...prev,
        media_files: [...(prev.media_files ?? []), ...newImages],
      }));

      newImages.forEach(({ file, preview }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (params?.id) {
          formData.append("item_id", params.id);
          formData.append("item_type", "service");
        }

        uploadImageMutation
          .mutateAsync(formData)
          .then((response) => {
            const saved = response?.data?.data;
            setRootFormDetails((prev) => ({
              ...prev,
              media_files: (prev.media_files ?? []).map((img) =>
                img.preview === preview
                  ? {
                      id: saved.id,
                      url: saved.url,
                      file_name: saved.file_name,
                      file: file,
                    }
                  : img,
              ),
            }));
            triggerToast("Image uploaded successfully", "success");
          })
          .catch(() => {
            setRootFormDetails((prev) => ({
              ...prev,
              media_files: (prev.media_files ?? []).filter(
                (img) => img.preview !== preview,
              ),
            }));
            triggerToast("Failed to upload image", "error");
          });
      });
    });
  };

  return {
    inputRef,
    deleteImage,
    serviceReorderImages,
    isUploadingImage,
    handleMultipleImageUpload,
  };
};
